import * as React from 'react';
import { connect } from 'react-redux';
import * as Metadata from '../../../entities/Metadata';
import * as ResourcesListStore from '../../../store/ResourcesListStore';
import { ITimelineProps } from '../../common/extensibleEntity/EntityTimelineList';
import { IRow } from '../../common/timeline/TimelineList';
import { Dictionary, EntityType, getCheckboxOptionsBasedOnCommands, IExtensibleEntity, ISourceInfo, ITimeframe, IWithScale, IWithSortBy, IWithSubViews, Quantization } from '../../../entities/common';
import { IObjectWithKey } from 'office-ui-fabric-react';
import WorkloadGridCell from './WorkloadGridCell';
import { IWithSearch, withSearch } from '../../common/withSearch';
import { IAssignmentByDay, IAssignmentByDayData, ITask, ITaskAttrs, ITaskBaselineByDay, ITaskByDay, sortTasksByRank } from '../../../entities/Subentities';
import { ApplicationState } from '../../../store';
import { ProjectInfo } from '../../../store/ProjectsListStore';
import { DebouncedAction, distinct, distinctBy, notEmpty, notUndefined, toDictionaryById, toDictionaryByName } from '../../utils/common';
import { ViewService } from '../../../services/ViewService';
import { FieldsState } from '../../../store/fields';
import { Resource } from '../../../store/ResourcesListStore';
import { getTimeTypes, WorkloadControlSettings } from './common';
import { IListProps } from '../../common/extensibleEntity/EntityDetailsList';
import BaseUsageGrid, { SegmentContentProps } from '../../resource/usage/ResourceUsageGrid/BaseUsageGrid';
import { AssignmentByDayUpdate } from '../../../store/tasks';
import { IOrderBy } from '../../../store/views';
import { getAssigneeCalendars } from '../../utils/duration';
import { nameof } from '../../../store/services/metadataService';
import { DisplayFieldService } from '../../common/DisplayFieldService';
import { CalendarDataSet } from '../../../store/calendar/common';

type TaskSubLine = IExtensibleEntity & { task: ITask; type: EntityType.Task };
type ResourceSubLine = IExtensibleEntity & { resource: ResourcesListStore.Resource, type: EntityType.Resource };
type SubLine = TaskSubLine | ResourceSubLine;

export type OwnProps = {
    readOnly?: boolean;
    controlSettings: WorkloadControlSettings;
    entity: ProjectInfo;
    tasks: ITask[];
    taskFakeFields?: Metadata.Field[];
    calendar: CalendarDataSet;
    calendarsById: Dictionary<CalendarDataSet>;
    updateAssignmentByDay: (updates: AssignmentByDayUpdate[]) => void;
    listProps: Partial<IListProps> & Partial<ITimelineProps>;
}

type StoreProps = {
    fields: Dictionary<FieldsState>
    taskbyDays: Dictionary<ITaskByDay[]>;
    taskBaselinebyDays: Dictionary<ITaskBaselineByDay[]>;
    assignmentsByDay: IAssignmentByDay[];
    isLoading: boolean;
    resources: Dictionary<Resource>;
}

const toAssignmentByDayId = (taskId: string, resourceId: string) => `${taskId}___${resourceId}`;
const toSubRowId = (parentId: string, childId: string) => `${parentId}_${childId}`;

type ResourceFieldProps = {
    field: Metadata.Field;
    byDays: IAssignmentByDayData[];
    workType: Exclude<keyof IAssignmentByDayData, 'Date'>;
}

const ResourceWork = ({ byDays, field, workType }: ResourceFieldProps) => {
    const total = React.useMemo(() => byDays.reduce((acc, day) => acc + (day[workType] ?? 0), 0), [byDays, workType]);
    const Formatter = React.useMemo(() => DisplayFieldService.getFieldFormatter(field, false), [field]);
    if (Formatter) {
        return <Formatter value={total}/>;
    }
    return <>{total}</>;
}

const WorkloadGrid = (props: OwnProps & StoreProps & IWithSearch) => {
    const { entity, isLoading, controlSettings, taskbyDays, taskBaselinebyDays, assignmentsByDay, fields, resources, taskFakeFields } = props;
    const timeType = React.useMemo(() => getTimeTypes(controlSettings), [controlSettings.usage?.timeType]);

    const tasks = React.useMemo(() => (props.tasks ?? []).sort((a: ITask, b: ITask) => sortTasksByRank(a, b)), [props.tasks]);
    const tasksMap = React.useMemo(() => toDictionaryById(props.tasks ?? []), [props.tasks]);
    const { assignmentByDayMap, resourceByDayMap, taskIdsByResourceId } = React.useMemo(() =>
        assignmentsByDay.reduce<{ assignmentByDayMap: Dictionary<IAssignmentByDay[]>, resourceByDayMap: Dictionary<Dictionary<IAssignmentByDayData>>, taskIdsByResourceId: Dictionary<string[]> }>((acc, item) => {
            const task = tasksMap[item.attributes.Task.id];
            if(!task || task.hierarchy?.isParent){
                return acc;
            }

            const id = toAssignmentByDayId(item.attributes.Task.id, item.attributes.Resource.id);
            if (!acc.assignmentByDayMap[id]) {
                acc.assignmentByDayMap[id] = [];
            }
            acc.assignmentByDayMap[id].push(item);
            if (!acc.resourceByDayMap[item.attributes.Resource.id]) {
                acc.resourceByDayMap[item.attributes.Resource.id] = {};
            }
            if (!acc.resourceByDayMap[item.attributes.Resource.id][String(item.attributes.Date)]) {
                acc.resourceByDayMap[item.attributes.Resource.id][String(item.attributes.Date)] = { 
                    Date: item.attributes.Date, 
                    Effort: item.attributes.Effort, 
                    CompletedWork: item.attributes.CompletedWork, 
                    RemainingWork: item.attributes.RemainingWork 
                };
            } else {
                const existing = acc.resourceByDayMap[item.attributes.Resource.id][String(item.attributes.Date)];

                acc.resourceByDayMap[item.attributes.Resource.id][String(item.attributes.Date)].Effort = existing.Effort === null && item.attributes.Effort === null 
                    ? null 
                    : (existing.Effort ?? 0) + (item.attributes.Effort ?? 0);
                acc.resourceByDayMap[item.attributes.Resource.id][String(item.attributes.Date)].CompletedWork += item.attributes.CompletedWork ?? 0;
                acc.resourceByDayMap[item.attributes.Resource.id][String(item.attributes.Date)].RemainingWork = existing.RemainingWork === null && item.attributes.RemainingWork === null
                    ? null
                    : (existing.RemainingWork ?? 0) + (item.attributes.RemainingWork ?? 0);
            }

            if (!acc.taskIdsByResourceId[item.attributes.Resource.id]) {
                acc.taskIdsByResourceId[item.attributes.Resource.id] = [];
            }
            if (!acc.taskIdsByResourceId[item.attributes.Resource.id].includes(item.attributes.Task.id)) {
                acc.taskIdsByResourceId[item.attributes.Resource.id].push(item.attributes.Task.id);
            }
            return acc;
        }, { assignmentByDayMap: {}, resourceByDayMap: {}, taskIdsByResourceId: {} }
        ), [assignmentsByDay, tasksMap]);

    const buildRow = React.useCallback((_: IExtensibleEntity) =>
        controlSettings.usage.grouping === 'Resource'
            ? BuildResourceRow(_ as Resource, tasksMap, taskIdsByResourceId)
            : BuildTaskRow(_ as ITask, resources),
        [resources, assignmentsByDay, controlSettings.usage.grouping, tasksMap, taskIdsByResourceId]);

    const fieldsMap = React.useMemo(() => Object.keys(fields)
        .filter(_ => [EntityType.Task, EntityType.Resource].includes(_ as EntityType))
        .reduce<Dictionary<Dictionary<Metadata.Field>>>((acc, currentEntityType) => {
            acc[currentEntityType] = toDictionaryByName(fields[currentEntityType].allIds.map(_ => fields[currentEntityType].byId[_]));
            if (currentEntityType === EntityType.Task) {
                taskFakeFields?.forEach(_ => {
                    acc[currentEntityType][_.name] = _;
                });
            }
            return acc;
        }, {}), [fields, taskFakeFields]);

    const debouncedUpdateAssignmentByDay = React.useMemo(() => new DebouncedAction(
        (data: AssignmentByDayUpdate[]) => {
            if (data) {
                data.reverse();
                data = distinctBy(data, _ => `${_.taskId}_${_.resourceId}_${_.date}`);
                data.reverse();

                props.updateAssignmentByDay(data);
            }
        }), [props.updateAssignmentByDay]);

    const resourceIds = tasks.filter(_ => !_.hierarchy?.isParent).flatMap(_ => _.attributes.AssignedTo).map(_ => _?.id).filter(notUndefined).filter(distinct);
    
    const entityType = EntityType.Task;
    const listProps: IListProps & Partial<ITimelineProps> = {
        ...props.listProps,
        isSearchApplied: !!props.search?.search?.searchText,
        buildTree: true,
        expandedEntitiesIds: [],
        primaryOutlineLevel: 1,
        buildRow: buildRow,
        entities: controlSettings.usage.grouping === 'Resource' ? resourceIds.map(_ => resources[_]) : tasks,
        entityType: entityType,
        fields: Object.values(fieldsMap[entityType]),
        displayFields: controlSettings.usage.displayFields,
        onItemRender: (subline: SubLine, _, field, defaultRender) => {
            const lineEntityType = subline.type;

            if(controlSettings.usage.grouping === 'Resource' || lineEntityType === EntityType.Resource) {
                const bydays = controlSettings.usage.grouping === 'Resource'
                    ? lineEntityType === EntityType.Resource
                        ? Object.values(resourceByDayMap[subline.resource.id] || [])
                        : (assignmentByDayMap[toAssignmentByDayId(subline.id, subline.attributes.resourceId)] ?? []).map(a => a.attributes)
                    : lineEntityType === EntityType.Resource
                        ? (assignmentByDayMap[toAssignmentByDayId(subline.attributes.taskId, subline.id)] ?? []).map(a => a.attributes)
                        : (assignmentByDayMap[toAssignmentByDayId(subline.id, subline.attributes.resourceId)] ?? []).map(a => a.attributes);
                if(field.name === nameof<ITaskAttrs>("Effort")) {
                    return <ResourceWork workType="Effort" byDays={bydays} field={field} />
                }
                if(field.name === nameof<ITaskAttrs>("CompletedWork")) {
                    return <ResourceWork workType="CompletedWork" byDays={bydays} field={field} />
                }
                if(field.name === nameof<ITaskAttrs>("RemainingWork")) {
                    return <ResourceWork workType="RemainingWork" byDays={bydays} field={field} />
                }
            }

            const lineEntity = lineEntityType === EntityType.Task
                ? subline.task
                : subline.resource;
            const entityColumn = ViewService.buildColumn(field.name, fieldsMap[lineEntityType], undefined, true, lineEntityType);
            return entityColumn ? entityColumn.onRender?.(lineEntity, undefined, entityColumn) : null;
        },
        extractEntityForSorting: (row: IRow, orderBy: IOrderBy | IOrderBy[] | undefined) => {
            const subline = row.entity as SubLine;
            return subline.type === EntityType.Resource ? subline.resource : subline.task;
        },
        ...getCheckboxOptionsBasedOnCommands([])
    };

    return <BaseUsageGrid
        scrollable={false}
        entitiesIds={controlSettings.usage.grouping === 'Resource'
            ? resourceIds.flatMap(rId => (taskIdsByResourceId[rId] ?? []).map(tId => toSubRowId(rId, tId)))
            : tasks.filter(_ => !_.hierarchy?.isParent).flatMap(task => (task.attributes.AssignedTo ?? []).map(a => a?.id ? toSubRowId(task.id, a.id) : null).filter(notEmpty))}
        selectedEntityIds={[]}
        isLoading={isLoading}
        listProps={listProps}
        isEditable={_entity => !props.readOnly && (
            ((_entity as SubLine).type === EntityType.Resource && controlSettings.usage.grouping === 'Task')
            || ((_entity as SubLine).type === EntityType.Task && controlSettings.usage.grouping === 'Resource'))}
        buildRowMenuItems={_ => []}
        renderSegmentContent={(segmentProps: SegmentContentProps) => {
            const { row, segment, quantization, selectionIndex, cellSelection, componentRef, onClick, onKeyDown, onKeyPress, onEditStart, onEditCompleteBuilder } = segmentProps;

            const resourceId = row.rowType === EntityType.Resource ? row.entity.id : row.entity.attributes.resourceId;
            const taskId = row.rowType === EntityType.Resource ? row.entity.attributes.taskId : row.entity.id;
            const bydays = controlSettings.usage.grouping === 'Resource'
                ? row.rowType === EntityType.Resource
                    ? Object.values(resourceByDayMap[resourceId] || [])
                    : (assignmentByDayMap[toAssignmentByDayId(taskId, resourceId)] ?? []).map(a => a.attributes)
                : row.rowType === EntityType.Resource
                    ? (assignmentByDayMap[toAssignmentByDayId(taskId, resourceId)] ?? []).map(a => a.attributes)
                    : (taskbyDays[taskId] ?? []).map(t => t.attributes);

            const onEditComplete = onEditCompleteBuilder(
                (effort: number | null, completedWork: number | null, remainingWork: number | null) => {
                    const cell = (cellSelection.getItems() as ISelectableCellData[]).find(_ => _.key === selectionIndex);
                    if (cell) {
                        const update = {
                            resourceId,
                            taskId,
                            date: cell.start.toDateOnlyString(),
                            effort,
                            completedWork,
                            remainingWork
                        };
                        debouncedUpdateAssignmentByDay.callAction(update);
                    }
                });
                   
            const rowCalendars = !entity.settings.useResourceCalendar || quantization !== Quantization.days
                ? undefined
                : row.rowType === EntityType.Resource
                    ? [ResourcesListStore.createResourceCalendar(resources[resourceId], props.calendarsById[resources[resourceId].calendar.id])]
                    : tasksMap[taskId].hierarchy?.isParent
                        ? [props.calendar]
                        : getAssigneeCalendars(entity.settings.useResourceCalendar, tasksMap[taskId]?.attributes.AssignedTo, props.calendar, resources, props.calendarsById);

            return <WorkloadGridCell
                byDays={bydays}
                taskBaselineByDays={row.rowType === EntityType.Task ? taskBaselinebyDays[taskId] : undefined}
                timeType={timeType}
                selection={cellSelection}
                selectionIndex={selectionIndex}
                componentRef={componentRef}
                onClick={onClick}
                onKeyDown={onKeyDown}
                onKeyPress={onKeyPress}
                onEditStart={onEditStart}
                onEditComplete={onEditComplete}
                quantization={quantization}
                startDate={segment.startDate}
                finishDate={segment.finishDate}
                isNonWorking={!!rowCalendars && !segment.startDate.isWorkingDay(...rowCalendars)}
            />;
        }}
        timeTypeLength={timeType.length}
    />;
}
export interface ISelectableCellData extends IObjectWithKey {
    entityId: string;
    start: Date;
    finish: Date;
}
const BuildTaskRow = (task: ITask, resources: Dictionary<Resource>): IRow => {
    const result = {
        key: task.id,
        entity: { id: task.id, key: task.id, attributes: {}, task, type: EntityType.Task } as SubLine,
        subItemType: EntityType.Resource,
        rowType: EntityType.Task,
        markers: [],
        segments: [],
        subItems: task.hierarchy?.isParent ? [] : (task.attributes.AssignedTo ?? []).map(assignment => ({
            key: toSubRowId(task.id, assignment.id),
            entity: { id: assignment.id, key: assignment.id, attributes: { taskId: task.id }, resource: resources[assignment.id], type: EntityType.Resource } as SubLine,
            rowType: EntityType.Resource,
            markers: [],
            segments: []
        }))
    }
    return result;
}
const BuildResourceRow = (resource: Resource, tasks: Dictionary<ITask>, taskIdsByResourceId: Dictionary<string[]>): IRow => {
    const result = {
        key: resource.id,
        entity: { id: resource.id, key: resource.id, attributes: {}, resource, type: EntityType.Resource } as SubLine,
        subItemType: EntityType.Task,
        rowType: EntityType.Resource,
        markers: [],
        segments: [],
        subItems: taskIdsByResourceId[resource.id]?.map(taskId => ({
            key: toSubRowId(resource.id, taskId),
            entity: { id: taskId, key: taskId, attributes: { resourceId: resource.id }, task: tasks[taskId], type: EntityType.Task } as SubLine,
            rowType: EntityType.Task,
            markers: [],
            segments: []
        })) ?? []
    }
    return result;
}

function mapStateToProps(state: ApplicationState, ownProps: OwnProps): StoreProps {
    return {
        fields: state.fields,
        isLoading: state.resources.isLoading || state.resources.isListLoading
            || state.projectsList.taskByDays.isLoading || state.projectsList.taskByDays.isListLoading
            || state.projectsList.assignmentByDays.isLoading || state.projectsList.assignmentByDays.isListLoading
            || state.projectsList.taskBaselineByDays.isLoading || state.projectsList.taskBaselineByDays.isListLoading,
        resources: state.resources.byId,
        taskbyDays: ownProps.entity.taskByDays ?? {},
        taskBaselinebyDays: ownProps.entity.taskBaselineByDays ?? {},
        assignmentsByDay: ownProps.entity.assignmentByDays ?? []
    };
}

export default connect(mapStateToProps)(withSearch(WorkloadGrid));